import React, { useState, useMemo, useEffect, useRef } from 'react';
import EventNotification from '../../../interfaces/eventNotification/EventNotification';
import { getTimeAgo } from '../../../utilities/dateUtils';
import AvatarImage from '../../ui/Avatar/AvatarImage';
import './NotificationPanel.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell, faChevronDown, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../firebase/AuthProvider';
import { markNotificationRead } from '../../../services/notificationServices/NotificationService';
import { Dropdown } from 'react-bootstrap';
import { renderNotificationMessage, renderNotificationTitle } from '../../../utilities/notifications/notificationUtils';
import { useNotificationState } from '../../../contexts/NotificationStateContext';

interface NotificationPanelProps {
    notifications: EventNotification[];
    onClose: () => void;
    isOpen: boolean;
}

type FilterType = 'today' | 'this_week' | 'earlier';

const NotificationPanel: React.FC<NotificationPanelProps> = ({ notifications, onClose, isOpen }) => {
    const [activeFilter, setActiveFilter] = useState<FilterType>('today');
    const [displayCount, setDisplayCount] = useState<number>(5);
    const notificationListRef = React.useRef<HTMLDivElement>(null);
    const navigate = useNavigate();
    const { currentUser } = useAuth();
    const { markNotificationAsReadById } = useNotificationState();

    // Track previous open state to only trigger logic on "Open" action
    const prevOpenRef = useRef(isOpen);

    // Smart Filter Selection Effect
    useEffect(() => {
        // Only run this logic when the panel transitions from closed to open
        if (isOpen && !prevOpenRef.current) {
            const now = new Date();
            const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            const startOfSevenDaysAgo = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6);

            // 1. Check if there is anything for TODAY (Read or Unread)
            const hasTodayItems = notifications.some(n =>
                new Date(n.createdAt) >= startOfToday
            );

            if (hasTodayItems) {
                setActiveFilter('today');
            } else {
                // 2. If nothing today, check if there are UNREAD items this week
                const hasUnreadThisWeek = notifications.some(n => {
                    const d = new Date(n.createdAt);
                    return !n.read && d >= startOfSevenDaysAgo && d <= now;
                });

                if (hasUnreadThisWeek) {
                    setActiveFilter('this_week');
                } else {
                    // 3. If nothing unread this week, check if there are UNREAD items earlier
                    const hasUnreadEarlier = notifications.some(n => {
                        const d = new Date(n.createdAt);
                        return !n.read && d < startOfSevenDaysAgo;
                    });

                    if (hasUnreadEarlier) {
                        setActiveFilter('earlier');
                    } else {
                        // 4. Fallback to Today (will show "You're all caught up")
                        setActiveFilter('today');
                    }
                }
            }

            // Always reset display count when reopening
            setDisplayCount(5);
        }

        // Update ref for next render
        prevOpenRef.current = isOpen;
    }, [isOpen, notifications]);

    const handleNotificationClick = async (notification: EventNotification, e: React.MouseEvent<HTMLDivElement>) => {
        // If the click is on a link, let the link handle the navigation
        if ((e.target as HTMLElement).closest('a')) {
            onClose();
            return;
        }

        // Otherwise, proceed with the default behavior
        if (!notification.read && currentUser) {
            try {
                const authToken = await currentUser.getIdToken();
                markNotificationAsReadById(notification.id);
                await markNotificationRead(authToken, notification.id);
            } catch (error) {
                console.error("Failed to mark notification as read:", error);
            }
        }
        navigate(`/profile/notifications?message=${notification.id}`);
        onClose();
    };

    const filteredNotifications = useMemo(() => {

        const uniqueNotifications = Array.from(
            new Map(notifications.map(item => [item.id, item])).values()
        );

        const now = new Date();
        const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        // startOfSevenDaysAgo is midnight 6 days before today => covers 7 full days including today
        const startOfSevenDaysAgo = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6);

        return uniqueNotifications.filter(n => {
            const notificationDate = new Date(n.createdAt);
            if (activeFilter === 'today') {
                return notificationDate >= startOfToday;
            }
            if (activeFilter === 'this_week') {
                // include notifications from startOfSevenDaysAgo up through now (includes today's notifications)
                return notificationDate >= startOfSevenDaysAgo && notificationDate <= now;
            }
            if (activeFilter === 'earlier') {
                return notificationDate < startOfSevenDaysAgo;
            }
            return false;
        }).sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    }, [notifications, activeFilter]);

    // Reset display count when filter changes (User manually changes filter)
    React.useEffect(() => {
        setDisplayCount(5);
    }, [activeFilter]);

    const displayedNotifications = useMemo(() => {
        return filteredNotifications.slice(0, displayCount);
    }, [filteredNotifications, displayCount]);

    const hasMoreNotifications = filteredNotifications.length > displayCount;

    const handleSeeMore = () => {
        const previousCount = displayCount;
        const newCount = Math.min(displayCount + 5, filteredNotifications.length);
        setDisplayCount(newCount);

        // Scroll to the newly added notifications after state update
        setTimeout(() => {
            if (notificationListRef.current) {
                const notificationItems = notificationListRef.current.querySelectorAll('.notification-item');
                if (notificationItems[previousCount]) {
                    notificationItems[previousCount].scrollIntoView({
                        behavior: 'smooth',
                        block: 'nearest'
                    });
                }
            }
        }, 0);
    };

    const unreadTodayCount = useMemo(() => {
        const now = new Date();
        const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        return notifications.filter(n => {
            const d = new Date(n.createdAt);
            return !n.read && d >= startOfToday;
        }).length;
    }, [notifications]);

    const unreadThisWeekCount = useMemo(() => {
        const now = new Date();
        const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const startOfSevenDaysAgo = new Date(startOfToday);
        startOfSevenDaysAgo.setDate(startOfSevenDaysAgo.getDate() - 6); // last 7 days including today

        return notifications.filter(n => {
            const d = new Date(n.createdAt);
            // Include notifications from startOfSevenDaysAgo through now (includes today)
            return !n.read && d >= startOfSevenDaysAgo && d <= now;
        }).length;
    }, [notifications]);

    const getFilterLabel = (filter: FilterType) => {
        switch (filter) {
            case 'today':
                return `Today${unreadTodayCount > 0 ? ` (${unreadTodayCount})` : ''}`;
            case 'this_week':
                return `This Week${unreadThisWeekCount > 0 ? ` (${unreadThisWeekCount})` : ''}`;
            case 'earlier':
                return 'Earlier';
            default:
                return 'Today';
        }
    };

    const handleViewAll = () => {
        navigate('/profile/notifications');
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="notification-panel-overlay" onClick={onClose}>
            <div className="notification-panel" onClick={(e) => e.stopPropagation()}>
                <div className="notification-panel-header">
                    <h2>Notifications</h2>
                </div>
                <div className="notification-filters">
                    <Dropdown className="notification-filter-dropdown w-45">
                        <Dropdown.Toggle
                            variant="light"
                            className="notification-dropdown-toggle"
                            id="notification-filter-dropdown"
                        >
                            {getFilterLabel(activeFilter)}
                            <FontAwesomeIcon icon={faChevronDown} className="ms-2" style={{ fontSize: '12px' }} />
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                            <Dropdown.Item
                                onClick={() => setActiveFilter('today')}
                                active={activeFilter === 'today'}
                            >
                                Today{unreadTodayCount > 0 ? ` (${unreadTodayCount})` : ''}
                            </Dropdown.Item>
                            <Dropdown.Item
                                onClick={() => setActiveFilter('this_week')}
                                active={activeFilter === 'this_week'}
                            >
                                This Week{unreadThisWeekCount > 0 ? ` (${unreadThisWeekCount})` : ''}
                            </Dropdown.Item>
                            <Dropdown.Item
                                onClick={() => setActiveFilter('earlier')}
                                active={activeFilter === 'earlier'}
                            >
                                Earlier
                            </Dropdown.Item>
                        </Dropdown.Menu>
                    </Dropdown>
                    <div
                        className="notification-view-all-link"
                        onClick={handleViewAll}
                    >
                        View All
                        <FontAwesomeIcon icon={faChevronRight} className="ms-1" style={{ fontSize: '12px' }} />
                    </div>
                </div>
                <div className="notification-list" ref={notificationListRef}>
                    {filteredNotifications.length > 0 ? (
                        <>
                            {displayedNotifications.map(notification => (
                                <div
                                    key={notification.id}
                                    className={`notification-item ${!notification.read ? 'unread' : 'read'}
                                    border-l-2 ${!notification.read ? 'border-[#7E57C2]' : 'border-[#d5c8f0]'}
                                    `}
                                    onClick={(e) => handleNotificationClick(notification, e)}
                                >
                                    <div className="notification-avatar">
                                        {notification.createdByUser ? (
                                            <AvatarImage userId={notification.createdByUser.id} size={"sm"} />
                                        ) : (
                                            <div className="default-avatar-icon">
                                                <FontAwesomeIcon icon={faBell} />
                                            </div>
                                        )}
                                    </div>
                                    <div className="notification-content">
                                        <div className="notification-header">
                                            {renderNotificationTitle(notification)}
                                        </div>
                                        <div className="notification-text">
                                            {renderNotificationMessage(notification)}
                                        </div>
                                        <span className="notification-time">{getTimeAgo(notification.createdAt)}</span>
                                    </div>
                                    {/* {!notification.read && <div className="bg-red-500 unread-dot"></div>} */}
                                </div>
                            ))}
                            {hasMoreNotifications && (
                                <div className="notification-see-more-container">
                                    <button
                                        className="notification-see-more-button"
                                        onClick={handleSeeMore}
                                    >
                                        See More ({filteredNotifications.length - displayCount} remaining)
                                    </button>
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="empty-notifications">
                            <div className="empty-icon">
                                ✨
                            </div>
                            <h3>You're all caught up</h3>
                            <p>All notifications will be displayed here</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default NotificationPanel;